import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import QuotaCard from "./QuotaCard";
import {
  bandwidthQuery,
  currentMonthBandwidthQuery,
  encodingPlanQuery,
} from "./usage.queries";
import { formatBytes, formatDuration } from "../charts/charts.utils";

import type { JSX } from "react";

import type { UsagePeriodProps } from "./usage.types";

function QuotasSection({ period }: UsagePeriodProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const currentMonthResult = useQuery(currentMonthBandwidthQuery());
  const bandwidthResult = useQuery(bandwidthQuery(period));
  const encodingPlanResult = useQuery(encodingPlanQuery());

  return (
    <section aria-labelledby="usage-quotas" className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold" id="usage-quotas">
        {t("usage.quotas")}
      </h2>
      <div className="grid gap-4 @3xl:grid-cols-2">
        <QuotaCard
          formatAmount={(value) => formatBytes(value, lang)}
          label={t("usage.quota.monthlyBandwidth")}
          max={bandwidthResult.data?.allowed_bandwidth ?? 0}
          maxCaptionKey="usage.quota.ofMax"
          used={currentMonthResult.data?.[0]?.bandwidth ?? 0}
          query={{
            isLoading:
              currentMonthResult.isLoading || bandwidthResult.isLoading,
            isFetching:
              currentMonthResult.isFetching || bandwidthResult.isFetching,
            isError: currentMonthResult.isError || bandwidthResult.isError,
          }}
        />
        <QuotaCard
          formatAmount={formatDuration}
          label={t("usage.quota.transcoding")}
          max={encodingPlanResult.data?.max_encoding_sec ?? 0}
          maxCaptionKey="usage.quota.ofContract"
          query={encodingPlanResult}
          used={encodingPlanResult.data?.encoding_sec ?? 0}
        />
      </div>
    </section>
  );
}

export default QuotasSection;
